import React from "react";
import Seo from "../components/Seo";
import { Section, Eyebrow } from "../components/primitives";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import SmartScaleMarquee from "../components/SmartScaleMarquee";
import {
  Bluetooth,
  Code2,
  Boxes,
  Cpu,
  HardDrive,
  Handshake,
  Truck,
  Network,
  ArrowRight,
  Mail,
} from "lucide-react";
import { PARTNERSHIP_EMAIL, IMAGES } from "../lib/content";

const opportunities = [
  { icon: Bluetooth, t: "Official Bluetooth Compatibility", d: "Certified, secure device connections within the Jollof Fit Kingdom ecosystem." },
  { icon: Code2, t: "API Integrations", d: "Robust APIs to exchange health and device data seamlessly." },
  { icon: Boxes, t: "SDK Integrations", d: "Native SDKs to bring Jollof Fit Kingdom into your products." },
  { icon: Cpu, t: "OEM Partnerships", d: "White-label and OEM opportunities powered by our platform." },
  { icon: HardDrive, t: "Hardware Partnerships", d: "Co-develop connected devices for the next generation of fitness." },
  { icon: Network, t: "Technical Collaboration", d: "Joint engineering to unlock deeper, smarter integrations." },
  { icon: Truck, t: "Distribution Opportunities", d: "Reach a growing global audience of engaged health users." },
  { icon: Handshake, t: "Strategic Partnerships", d: "Long-term alliances to shape the connected health future together." },
];

export default function BusinessPartnerships() {
  return (
    <div>
      <Seo
        title="Business Partnerships"
        path="/business-partnerships"
        noindex
        description="Partner with Jollof Fit Kingdom. We're seeking long-term partnerships with smart health device manufacturers, wearable companies and connected fitness brands for Bluetooth compatibility, API/SDK/OEM and hardware integrations."
      />

      {/* Hero */}
      <section className="relative overflow-hidden pt-40 pb-16 md:pt-48">
        <div className="pointer-events-none absolute inset-0 radial-fade" />
        <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" />
        <Section className="relative text-center">
          <Reveal>
            <img src={IMAGES.jfLogo} alt="Jollof Fit Kingdom" className="mx-auto mb-8 h-20 w-20 rounded-full" />
            <Eyebrow>Business Partnerships</Eyebrow>
            <h1 className="mx-auto mt-5 max-w-4xl font-heading text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Partner With <span className="text-gold-gradient">Jollof Fit Kingdom</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-zinc-400 md:text-lg">
              Jollof Fit Kingdom is actively seeking long-term partnerships with smart health device manufacturers, wearable
              companies and connected fitness brands to build the future connected health ecosystem — together.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <a href={`mailto:${PARTNERSHIP_EMAIL}`} data-testid="partnership-hero-cta" className="btn-gold">
                <Mail className="h-4 w-4" /> Start a Partnership
              </a>
              <a href="#opportunities" className="btn-secondary">
                Explore Opportunities <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </Section>
      </section>

      {/* Marquee */}
      <Section className="mt-6">
        <Reveal>
          <p className="mb-8 text-center text-xs font-bold uppercase tracking-[0.25em] text-zinc-600">
            Designed to support connected wellness technology
          </p>
        </Reveal>
        <SmartScaleMarquee />
      </Section>

      {/* Who we are */}
      <Section className="mt-28">
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Who We Are</Eyebrow>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-white md:text-4xl">
              A complete fitness ecosystem
            </h2>
            <p className="mt-5 text-base leading-relaxed text-zinc-400">
              Jollof Fit Kingdom combines training, nutrition, connected health, smart devices and personalized
              coaching into one complete ecosystem for people around the world. Our mission is to build the future of
              fitness — where every workout, meal, step, measurement and health insight works together.
            </p>
            <p className="mt-4 text-base leading-relaxed text-zinc-400">
              For hardware and platform partners, that means a premium experience that turns your device's
              data into lasting user value — deepening engagement, retention and brand affinity.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid gap-5 sm:grid-cols-2">
              {[
                { k: "Deeper engagement", v: "Your device data becomes personalized coaching users return to daily." },
                { k: "Premium positioning", v: "Associate your brand with a luxury, global health platform." },
                { k: "Faster integration", v: "Clear APIs and SDKs designed for reliable device connectivity." },
                { k: "Shared growth", v: "Reach an engaged, worldwide audience of health-focused users." },
              ].map((b) => (
                <div key={b.k} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
                  <p className="font-heading text-base text-white">{b.k}</p>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-400">{b.v}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </Section>

      {/* Opportunities */}
      <Section id="opportunities" className="mt-28">
        <div className="max-w-2xl">
          <Reveal>
            <Eyebrow>Collaboration</Eyebrow>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-white md:text-4xl">
              Ways we can build together
            </h2>
            <p className="mt-5 text-base text-zinc-400">
              From Bluetooth compatibility to deep technical collaboration, we're open to the partnerships that move
              connected health forward.
            </p>
          </Reveal>
        </div>
        <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {opportunities.map((o) => (
            <StaggerItem key={o.t}>
              <div
                data-testid={`opportunity-${o.t.toLowerCase().replace(/\s+/g, "-")}`}
                className="h-full rounded-2xl border border-white/10 bg-[#0a0a0a] p-7 transition-colors duration-300 hover:border-gold/30"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-gold">
                  <o.icon className="h-5 w-5" strokeWidth={1.6} />
                </div>
                <h3 className="mt-5 font-heading text-base text-white">{o.t}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-zinc-400">{o.d}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>

      {/* Why partner */}
      <Section className="mt-28">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { k: "Global", v: "A worldwide platform built to scale across regions and devices." },
            { k: "Connected", v: "A platform that turns your hardware's data into premium user value." },
            { k: "Long-term", v: "A partner focused on lasting relationships, not quick wins." },
          ].map((s, i) => (
            <Reveal key={s.k} delay={i * 0.08}>
              <div className="rounded-2xl border border-gold/15 bg-gradient-to-b from-[#12100a] to-[#0a0a0a] p-8">
                <p className="font-heading text-2xl text-gold-gradient">{s.k}</p>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{s.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Contact block */}
      <Section className="mt-28 pb-8">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-gold/25 bg-gradient-to-b from-[#14110a] to-[#080808] px-8 py-16 text-center md:px-16">
            <div className="pointer-events-none absolute -top-24 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-gold/10 blur-3xl" />
            <div className="relative">
              <Eyebrow>Business Partnership Contact</Eyebrow>
              <h2 className="mx-auto mt-5 max-w-2xl font-heading text-3xl font-medium tracking-tight text-white md:text-4xl">
                Let's build the future of connected health
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-base text-zinc-400">
                Reach out to our partnerships team to discuss integrations, hardware collaboration and strategic
                opportunities.
              </p>
              <a
                href={`mailto:${PARTNERSHIP_EMAIL}`}
                data-testid="partnership-email"
                className="mt-9 inline-flex items-center gap-3 rounded-full border border-gold/40 bg-gold/10 px-8 py-4 font-heading text-lg text-gold-light transition-colors duration-300 hover:bg-gold/20"
              >
                <Mail className="h-5 w-5" /> {PARTNERSHIP_EMAIL}
              </a>
            </div>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
